import { gql } from "@apollo/client"

import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"

export type AskQuestionEvalMode = "MOCK" | "LIVE"
export type AskQuestionEvalCaseStatus = "PASSED" | "FAILED"

export type AskQuestionEvalSuiteInfo = {
  id: string
  title: string
  scenarioCount: number
}

export type AskQuestionEvalCaseResult = {
  scenarioId: string
  title: string
  status: AskQuestionEvalCaseStatus
  durationMs: number
  aiMessageId?: string
  question: string
  answer?: string
  aiAction?: string
  expected?: Record<string, unknown>
  actual?: Record<string, unknown>
  failures: string[]
  error?: string
}

export type AskQuestionEvalCustomScenario = {
  id: string
  title: string
  query: string
  customerId?: string
  stepTest?: {
    step: string
    msgType?: string
  }
  mock?: {
    answer?: {
      content?: string
      aiAction?: string
    }
  }
  assertions?: {
    expectedContentIncludes?: string[]
    expectedStatus?: string
    expectedAiAction?: string
    requiredSteps?: string[]
    forbiddenSteps?: string[]
  }
}

export type AskQuestionEvalRun = {
  _id: string
  suiteId: string
  suiteTitle: string
  mode: AskQuestionEvalMode
  customerId?: string
  status: AskQuestionEvalCaseStatus
  total: number
  passed: number
  failed: number
  durationMs: number
  cases: AskQuestionEvalCaseResult[]
  createdAt: string
  updatedAt: string
}

export type AskQuestionEvalRunPaginate = {
  docs: AskQuestionEvalRun[]
  totalDocs: number
  hasNextPage: boolean
  offset: number
  limit: number
}

export const DEFAULT_ASK_QUESTION_EVAL_SUITES: AskQuestionEvalSuiteInfo[] = [
  {
    id: "ask-question-core",
    title: "AskQuestion hazır testleri",
    scenarioCount: 2
  }
]

const LIST_ASK_QUESTION_EVAL_SUITES = gql`
  query listAskQuestionEvalSuites {
    listAskQuestionEvalSuites {
      success
      data {
        id
        title
        scenarioCount
      }
      message
      code
    }
  }
`

const LIST_ASK_QUESTION_EVAL_RUNS = gql`
  query listAskQuestionEvalRuns($data: AskQuestionEvalRunListInput) {
    listAskQuestionEvalRuns(data: $data) {
      success
      data {
        docs {
          _id
          suiteId
          suiteTitle
          mode
          customerId
          status
          total
          passed
          failed
          durationMs
          createdAt
          updatedAt
          cases {
            scenarioId
            title
            status
            durationMs
            aiMessageId
            question
            answer
            aiAction
            expected
            actual
            failures
            error
          }
        }
        totalDocs
        hasNextPage
        offset
        limit
      }
      message
      code
    }
  }
`

const RUN_ASK_QUESTION_EVAL_SUITE = gql`
  mutation runAskQuestionEvalSuite($data: AskQuestionEvalSuiteInput!) {
    runAskQuestionEvalSuite(data: $data) {
      success
      data {
        _id
        suiteId
        suiteTitle
        mode
        customerId
        status
        total
        passed
        failed
        durationMs
        createdAt
        updatedAt
        cases {
          scenarioId
          title
          status
          durationMs
          aiMessageId
          question
          answer
          aiAction
          expected
          actual
          failures
          error
        }
      }
      message
      code
    }
  }
`

export async function listAskQuestionEvalSuites(): Promise<
  ApiFormattedResponse<AskQuestionEvalSuiteInfo[]>
> {
  const response = await query<{
    listAskQuestionEvalSuites?: {
      data: AskQuestionEvalSuiteInfo[]
    }
  }>({ query: LIST_ASK_QUESTION_EVAL_SUITES, fetchPolicy: "network-only" })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data:
      Number(response.data?.listAskQuestionEvalSuites?.data?.length) > 0
        ? response.data?.listAskQuestionEvalSuites?.data
        : DEFAULT_ASK_QUESTION_EVAL_SUITES
  }
}

export async function listAskQuestionEvalRuns(params: {
  customerId?: string
  mode?: AskQuestionEvalMode
  offset?: number
  limit?: number
}): Promise<ApiFormattedResponse<AskQuestionEvalRunPaginate>> {
  const response = await query<{
    listAskQuestionEvalRuns?: {
      data: AskQuestionEvalRunPaginate
    }
  }>({
    query: LIST_ASK_QUESTION_EVAL_RUNS,
    fetchPolicy: "network-only",
    variables: { data: params }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.listAskQuestionEvalRuns?.data
  }
}

export async function runAskQuestionEvalSuite(params: {
  suiteId: string
  mode: AskQuestionEvalMode
  customerId?: string
  limit?: number
  scenarios?: AskQuestionEvalCustomScenario[]
  mockId?: string
  template?: string
  msgType?: string
}): Promise<ApiFormattedResponse<AskQuestionEvalRun>> {
  const response = await mutate<{
    runAskQuestionEvalSuite?: {
      data: AskQuestionEvalRun
    }
  }>({
    mutation: RUN_ASK_QUESTION_EVAL_SUITE,
    variables: { data: params }
  })

  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.runAskQuestionEvalSuite?.data
  }
}

const DELETE_ALL_RUNS = gql`
  mutation deleteAllAskQuestionEvalRuns($customerId: ID) {
    deleteAllAskQuestionEvalRuns(customerId: $customerId) {
      success
      data
      message
      code
    }
  }
`

const DELETE_MANY_RUNS = gql`
  mutation deleteManyAskQuestionEvalRuns($ids: [ID!]!) {
    deleteManyAskQuestionEvalRuns(ids: $ids) {
      success
      data
      message
      code
    }
  }
`

export async function deleteAllAskQuestionEvalRuns(
  customerId?: string
): Promise<ApiFormattedResponse<number>> {
  const response = await mutate<{
    deleteAllAskQuestionEvalRuns?: { data: number }
  }>({ mutation: DELETE_ALL_RUNS, variables: { customerId } })
  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.deleteAllAskQuestionEvalRuns?.data
  }
}

export async function deleteManyAskQuestionEvalRuns(
  ids: string[]
): Promise<ApiFormattedResponse<number>> {
  const response = await mutate<{
    deleteManyAskQuestionEvalRuns?: { data: number }
  }>({ mutation: DELETE_MANY_RUNS, variables: { ids } })
  if (!response.isSuccess) return { ...response, data: undefined }
  return {
    isSuccess: true,
    data: response.data?.deleteManyAskQuestionEvalRuns?.data
  }
}
